'use client';

import { Icon } from '@iconify/react';
import Link from 'next/link';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

const services: {
    icon: string;
    titleKey: string;
    descKey: string;
    href?: string;
    linkKey?: string;
}[] = [
    {
        icon: 'carbon:application-web',
        titleKey: 'Services.items.webApps.title',
        descKey: 'Services.items.webApps.description',
    },
    {
        icon: 'carbon:machine-learning-model',
        titleKey: 'Services.items.ai.title',
        descKey: 'Services.items.ai.description',
        href: '/agents',
        linkKey: 'Services.items.ai.link',
    },
    {
        icon: 'carbon:cloud-services',
        titleKey: 'Services.items.fullstack.title',
        descKey: 'Services.items.fullstack.description',
    },
    {
        icon: 'carbon:devices',
        titleKey: 'Services.items.responsive.title',
        descKey: 'Services.items.responsive.description',
    },
];

export function ServicesSection() {
    const { t } = useTranslation();
    const sectionRef = useRef<HTMLElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);

    // Entrance handled by the site-wide ScrollFx reveal system (CSS + IO).
    return (
        <section ref={sectionRef} className="mb-12 sm:mb-16">
            <div className="reveal flex items-center gap-3 mb-8">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon icon="carbon:cube" className="h-5 w-5 text-primary" />
                </div>
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">
                        {t('Services.sectionLabel')}
                    </p>
                    <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">
                        {t('Services.sectionTitle')}
                    </h2>
                    <p className="text-base text-slate-700 dark:text-muted-foreground mt-2 max-w-2xl">
                        {t('Services.sectionSubtitle')}
                    </p>
                </div>
            </div>

            <div ref={cardsRef} className="reveal-stagger border-t border-slate-200 dark:border-slate-800">
                {services.map((service, i) => (
                    <div
                        key={service.titleKey}
                        className="group grid gap-3 border-b border-slate-200 py-7 transition-colors dark:border-slate-800 md:grid-cols-[4rem_1fr_1.4fr] md:items-baseline md:gap-6"
                    >
                        <span className="font-mono text-sm font-bold text-primary">
                            {String(i + 1).padStart(2, '0')}
                        </span>
                        <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                            {t(service.titleKey)}
                        </h3>
                        <div>
                            <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                                {t(service.descKey)}
                            </p>
                            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-muted-foreground">
                                    {t(service.descKey.replace('description', 'bestFor'))}
                                </span>
                                {service.href && service.linkKey && (
                                    <Link
                                        href={service.href}
                                        className="whitespace-nowrap text-xs font-bold text-primary hover:underline"
                                    >
                                        {t(service.linkKey)} →
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}
